// Strategic Portfolio (VRIO analysis views)

export const fr: Record<string, string> = {
  'nav.item.strategicPortfolio': 'Portefeuille stratégique',
  'page.strategic-portfolio': 'Portefeuille stratégique',

  'sp.title': 'Portefeuille stratégique des ressources',
  'sp.subtitle': 'Médianes VRIO par ressource, tous évaluateurs confondus. Composite = moyenne pondérée des cinq dimensions ; ⚑ signale un désaccord entre évaluateurs (écart ≥ 2).',
  'sp.empty.title': 'Pas encore de données VRIO',
  'sp.empty.body': "Terminez d'abord l'étape 6 (notation VRIO). Dès que des ressources auront des notes VRIO, les vues du portefeuille stratégique apparaîtront ici.",

  'sp.dim.inimitability': 'Inimitabilité',
  'sp.dim.durability': 'Durabilité',
  'sp.dim.appropriability': 'Appropriabilité',
  'sp.dim.substitutability': 'Substituabilité',
  'sp.dim.competitiveSuperiority': 'Supériorité concurrentielle',

  'sp.filter.allCapabilities': 'Toutes les capacités',
  'sp.filter.toggleType': 'Afficher / masquer ce type',
  'sp.weights.button': 'Poids du composite',
  'sp.weights.custom': '(personnalisés)',
  'sp.weights.note': 'Les poids sont normalisés — seul leur rapport compte.',
  'sp.weights.reset': 'Réinitialiser',

  'sp.tab.quadrant': 'VRIO × Maturité',
  'sp.tab.explorer': 'Explorateur 5D',
  'sp.tab.capabilities': 'Force des capacités',

  'sp.quadrant.vrioThreshold': 'Seuil VRIO',
  'sp.quadrant.maturityThreshold': 'Seuil de maturité',
  'sp.quadrant.tableView': 'Vue tabulaire',
  'sp.quadrant.hideTable': 'Masquer le tableau',
  'sp.quadrant.excludedNote': 'ressource(s) non tracée(s) (sans note VRIO ou de maturité) — voir le tableau',
  'sp.quadrant.strengths': 'Forces à exploiter',
  'sp.quadrant.develop': 'Priorités de développement',
  'sp.quadrant.overinvest': 'Surinvestissement potentiel',
  'sp.quadrant.deprioritize': 'Déprioriser',
  'sp.axis.composite': 'Score VRIO composite',
  'sp.axis.maturity': 'Maturité',
  'sp.table.resource': 'Ressource',
  'sp.table.type': 'Type',

  'sp.tip.raters': 'évaluateur(s)',
  'sp.flag.disagree': 'Désaccord entre évaluateurs — écart',

  'sp.explorer.xAxis': 'Axe X',
  'sp.explorer.yAxis': 'Axe Y',
  'sp.explorer.zAxis': 'Axe Z',
  'sp.explorer.dotSize': 'Taille des points',
  'sp.explorer.dotColor': 'Couleur des points',
  'sp.explorer.autoRotate': 'Rotation auto',
  'sp.explorer.stopRotate': 'Arrêter la rotation',
  'sp.explorer.resetView': 'Réinitialiser la vue',
  'sp.explorer.dragNote': 'Faites glisser pour pivoter.',
  'sp.toggle.labels': 'Étiquettes',
  'sp.toggle.guides': 'Repères 3D',
  'sp.zoom.fit': 'Ajuster',
  'sp.zoom.in': 'Zoom avant',
  'sp.zoom.out': 'Zoom arrière',
  'sp.explorer.wheelNote': 'Molette pour zoomer.',
  'sp.labels.pinHint': 'Double-clic sur un point : épingler / retirer son étiquette.',
  'sp.labels.clear': 'Effacer',
  'sp.explorer.hiddenNote': 'ressource(s) masquée(s) (dimension non évaluée).',

  'sp.cap.sortBy': 'Trier par',
  'sp.cap.sort.score': 'Score de force',
  'sp.cap.sort.avgVrio': 'VRIO moyen',
  'sp.cap.sort.avgCrit': 'Criticité moyenne',
  'sp.cap.sort.highCount': 'Nb de ressources à VRIO élevé',
  'sp.cap.highThreshold': 'Seuil VRIO élevé',
  'sp.cap.formula': 'Score = 40 × part de VRIO élevés + 30 × VRIO moyen + 30 × criticité moyenne (normalisés)',
  'sp.cap.empty': "Aucune donnée de capacité. La force des capacités requiert des sélections de l'étape 5 (paires capacité–ressource) dont les ressources ont des notes VRIO.",
  'sp.cap.avgVrio': 'VRIO moyen',
  'sp.cap.weighted': '(pondéré par criticité)',
  'sp.cap.avgCrit': 'criticité moyenne',
  'sp.cap.highRes': 'ressource(s) à VRIO élevé',
  'sp.cap.showTop': 'Top 3',
  'sp.cap.showAll': 'Tout afficher',
  'sp.cap.scoreAria': 'Score de force',

  'sp.detail.dims': 'Dimensions VRIO (médiane)',
  'sp.detail.composite': 'Composite (pondéré)',
  'sp.detail.maturity': 'Maturité',
  'sp.detail.caps': 'Capacités liées (criticité)',
  'sp.detail.noCaps': "Aucune sélection de l'étape 5 ne lie cette ressource à une capacité.",
};

export const en: Record<string, string> = {
  'nav.item.strategicPortfolio': 'Strategic Portfolio',
  'page.strategic-portfolio': 'Strategic Portfolio',

  'sp.title': 'Strategic Resource Portfolio',
  'sp.subtitle': 'VRIO medians across raters, per resource. Composite = weighted mean of the five dimensions; ⚑ marks rater disagreement (spread ≥ 2).',
  'sp.empty.title': 'No VRIO Data Yet',
  'sp.empty.body': 'Complete Stage 6 (VRIO Scoring) first. Once resources carry VRIO ratings, the strategic portfolio views appear here.',

  'sp.dim.inimitability': 'Inimitability',
  'sp.dim.durability': 'Durability',
  'sp.dim.appropriability': 'Appropriability',
  'sp.dim.substitutability': 'Substitutability',
  'sp.dim.competitiveSuperiority': 'Competitive Superiority',

  'sp.filter.allCapabilities': 'All capabilities',
  'sp.filter.toggleType': 'Show / hide this type',
  'sp.weights.button': 'Composite weights',
  'sp.weights.custom': '(custom)',
  'sp.weights.note': 'Weights are normalized — only their ratio matters.',
  'sp.weights.reset': 'Reset',

  'sp.tab.quadrant': 'VRIO × Maturity',
  'sp.tab.explorer': '5-D Explorer',
  'sp.tab.capabilities': 'Capability Strength',

  'sp.quadrant.vrioThreshold': 'VRIO threshold',
  'sp.quadrant.maturityThreshold': 'Maturity threshold',
  'sp.quadrant.tableView': 'Table view',
  'sp.quadrant.hideTable': 'Hide table',
  'sp.quadrant.excludedNote': 'resource(s) not plotted (no VRIO or maturity rating) — see table',
  'sp.quadrant.strengths': 'Strengths to leverage',
  'sp.quadrant.develop': 'Development priorities',
  'sp.quadrant.overinvest': 'Potential overinvestment',
  'sp.quadrant.deprioritize': 'Deprioritize',
  'sp.axis.composite': 'Composite VRIO score',
  'sp.axis.maturity': 'Maturity',
  'sp.table.resource': 'Resource',
  'sp.table.type': 'Type',

  'sp.tip.raters': 'rater(s)',
  'sp.flag.disagree': 'Raters disagree — spread',

  'sp.explorer.xAxis': 'X axis',
  'sp.explorer.yAxis': 'Y axis',
  'sp.explorer.zAxis': 'Z axis',
  'sp.explorer.dotSize': 'Dot size',
  'sp.explorer.dotColor': 'Dot color',
  'sp.explorer.autoRotate': 'Auto-rotate',
  'sp.explorer.stopRotate': 'Stop rotation',
  'sp.explorer.resetView': 'Reset view',
  'sp.explorer.dragNote': 'Drag to rotate.',
  'sp.toggle.labels': 'Labels',
  'sp.toggle.guides': '3D guides',
  'sp.zoom.fit': 'Fit',
  'sp.zoom.in': 'Zoom in',
  'sp.zoom.out': 'Zoom out',
  'sp.explorer.wheelNote': 'Scroll to zoom.',
  'sp.labels.pinHint': 'Double-click a point to pin / unpin its label.',
  'sp.labels.clear': 'Clear',
  'sp.explorer.hiddenNote': 'resource(s) hidden (missing a mapped dimension).',

  'sp.cap.sortBy': 'Sort by',
  'sp.cap.sort.score': 'Strength score',
  'sp.cap.sort.avgVrio': 'Avg VRIO',
  'sp.cap.sort.avgCrit': 'Avg criticality',
  'sp.cap.sort.highCount': '# high-VRIO resources',
  'sp.cap.highThreshold': 'High-VRIO threshold',
  'sp.cap.formula': 'Score = 40 × high-VRIO share + 30 × avg VRIO + 30 × avg criticality (each normalized)',
  'sp.cap.empty': 'No capability data. Capability strength needs Stage 5 selections (capability–resource pairs) whose resources carry VRIO ratings.',
  'sp.cap.avgVrio': 'Avg VRIO',
  'sp.cap.weighted': '(criticality-weighted)',
  'sp.cap.avgCrit': 'avg criticality',
  'sp.cap.highRes': 'high-VRIO resource(s)',
  'sp.cap.showTop': 'Show top 3',
  'sp.cap.showAll': 'Show all',
  'sp.cap.scoreAria': 'Strength score',

  'sp.detail.dims': 'VRIO dimensions (median)',
  'sp.detail.composite': 'Composite (weighted)',
  'sp.detail.maturity': 'Maturity',
  'sp.detail.caps': 'Linked capabilities (criticality)',
  'sp.detail.noCaps': 'No Stage 5 selections link this resource to a capability.',
};
